import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  Receipt,
  Download,
  Search,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Loader2,
  RefreshCw,
  AlertCircle,
  CreditCard,
  FileText,
} from 'lucide-react';

const STATUS_BADGE = {
  paid: { label: 'Paye', className: 'bg-green-100 text-green-700' },
  pending: { label: 'En attente', className: 'bg-yellow-100 text-yellow-700' },
  overdue: { label: 'En retard', className: 'bg-red-100 text-red-700' },
  draft: { label: 'Brouillon', className: 'bg-slate-100 text-slate-500' },
  cancelled: { label: 'Annule', className: 'bg-slate-100 text-slate-500' },
  refunded: { label: 'Rembourse', className: 'bg-blue-100 text-blue-700' },
};

const Billing = () => {
  const [invoices, setInvoices] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [appFilter, setAppFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [portalLoading, setPortalLoading] = useState(false);
  const pageSize = 15;

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, page_size: pageSize };
      if (appFilter) params.app_code = appFilter;
      if (statusFilter) params.status = statusFilter;
      if (search) params.search = search;
      const res = await api.get('/api/billing', { params });
      const data = res.data;
      if (Array.isArray(data)) {
        setInvoices(data);
        setTotalCount(data.length);
      } else {
        setInvoices(data.items || data.invoices || []);
        setTotalCount(data.total || data.count || (data.items || data.invoices || []).length);
      }
    } catch (err) {
      setError('Impossible de charger les factures');
    } finally {
      setLoading(false);
    }
  }, [page, appFilter, statusFilter, search]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const openStripePortal = async () => {
    setPortalLoading(true);
    try {
      const res = await api.get('/api/billing/portal');
      if (res.data.url || res.data.portal_url) {
        window.open(res.data.url || res.data.portal_url, '_blank');
      } else {
        toast.error('URL du portail non disponible');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible d'ouvrir le portail de paiement");
    } finally {
      setPortalLoading(false);
    }
  };

  const downloadInvoice = async (invoice) => {
    try {
      const res = await api.get(`/api/billing/${invoice.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `facture-${invoice.number || invoice.invoice_number || invoice.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      if (invoice.pdf_url || invoice.invoice_url) {
        window.open(invoice.pdf_url || invoice.invoice_url, '_blank');
      } else {
        toast.error('Impossible de telecharger la facture');
      }
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize) || 1;
  const appNames = [...new Set(invoices.map((inv) => inv.app_code || inv.app?.code).filter(Boolean))];

  if (loading && invoices.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="h-8 bg-slate-100 rounded w-48 animate-pulse" />
          <div className="h-10 bg-slate-100 rounded w-48 animate-pulse" />
        </div>
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex items-center gap-4 p-4 border-b border-slate-100 animate-pulse">
              <div className="w-10 h-10 bg-slate-100 rounded" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-slate-100 rounded w-32" />
                <div className="h-3 bg-slate-100 rounded w-48" />
              </div>
              <div className="h-6 bg-slate-100 rounded w-20" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error && invoices.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <AlertCircle className="w-12 h-12 text-red-400 mb-4" />
        <h3 className="text-lg font-semibold text-slate-900 mb-2">Erreur</h3>
        <p className="text-slate-500 mb-6">{error}</p>
        <button onClick={fetchInvoices} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-nexus-600 text-white text-sm font-medium hover:bg-nexus-700 transition-colors">
          <RefreshCw className="w-4 h-4" /> Reessayer
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Facturation</h1>
          <p className="text-slate-500 text-sm mt-1">{totalCount} facture{totalCount !== 1 ? 's' : ''}</p>
        </div>
        <button
          onClick={openStripePortal}
          disabled={portalLoading}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-nexus-600 text-white text-sm font-medium hover:bg-nexus-700 disabled:opacity-50 transition-colors"
        >
          {portalLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
          Gerer les moyens de paiement
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Rechercher une facture..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-9 pr-4 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-nexus-500 focus:border-nexus-500"
          />
        </div>
        {appNames.length > 0 && (
          <div className="relative">
            <select
              value={appFilter}
              onChange={(e) => { setAppFilter(e.target.value); setPage(1); }}
              className="appearance-none pl-3 pr-8 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-nexus-500 focus:border-nexus-500 bg-white"
            >
              <option value="">Toutes les apps</option>
              {appNames.map((code) => (
                <option key={code} value={code}>{code}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          </div>
        )}
        <div className="relative">
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="appearance-none pl-3 pr-8 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-nexus-500 focus:border-nexus-500 bg-white"
          >
            <option value="">Tous les statuts</option>
            <option value="paid">Paye</option>
            <option value="pending">En attente</option>
            <option value="overdue">En retard</option>
            <option value="draft">Brouillon</option>
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        </div>
      </div>

      {/* Invoice Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left font-semibold text-slate-700 px-4 py-3">Facture</th>
                <th className="text-left font-semibold text-slate-700 px-4 py-3 hidden sm:table-cell">Application</th>
                <th className="text-left font-semibold text-slate-700 px-4 py-3 hidden md:table-cell">Date</th>
                <th className="text-right font-semibold text-slate-700 px-4 py-3">Montant TTC</th>
                <th className="text-left font-semibold text-slate-700 px-4 py-3">Statut</th>
                <th className="text-right font-semibold text-slate-700 px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {invoices.map((invoice) => {
                const status = invoice.status || 'paid';
                const statusInfo = STATUS_BADGE[status] || STATUS_BADGE.paid;
                return (
                  <tr key={invoice.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                          <FileText className="w-4 h-4 text-slate-500" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">#{invoice.number || invoice.invoice_number || invoice.id}</p>
                          <p className="text-xs text-slate-500 sm:hidden">{invoice.app_code || invoice.app?.name || '--'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600 hidden sm:table-cell capitalize">
                      {invoice.app_code || invoice.app?.name || '--'}
                    </td>
                    <td className="px-4 py-3 text-slate-600 hidden md:table-cell">
                      {invoice.invoice_date || invoice.date || invoice.created_at
                        ? new Date(invoice.invoice_date || invoice.date || invoice.created_at).toLocaleDateString('fr-FR')
                        : '--'}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-900">
                      {invoice.amount_ttc != null
                        ? invoice.amount_ttc.toFixed(2)
                        : invoice.amount != null
                        ? invoice.amount.toFixed(2)
                        : '--'}{' '}
                      EUR
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${statusInfo.className}`}>
                        {statusInfo.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => downloadInvoice(invoice)}
                        className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
                        title="Telecharger PDF"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
              {invoices.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-slate-500">
                    <Receipt className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                    <p>Aucune facture trouvee</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50">
            <p className="text-sm text-slate-500">
              Page {page} sur {totalPages}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 rounded-lg border border-slate-300 text-slate-600 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageNum = Math.max(1, Math.min(page - 2, totalPages - 4)) + i;
                if (pageNum > totalPages) return null;
                return (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    className={cn(
                      'w-8 h-8 rounded-lg text-sm font-medium transition-colors',
                      pageNum === page
                        ? 'bg-nexus-600 text-white'
                        : 'text-slate-600 hover:bg-white border border-slate-300'
                    )}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-1.5 rounded-lg border border-slate-300 text-slate-600 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Billing;
